import { useState, useRef, useEffect } from 'react'
import { translations, type Language } from './i18n/translations'
import { Toaster, toast } from 'react-hot-toast'
import { AdBanner } from './components/AdBanner'
import { flagUrls } from './i18n/flags'

function App() {
  const [language, setLanguage] = useState<Language>('fr')
  const [url, setUrl] = useState('')
  const [affId, setAffId] = useState('')
  const [generatedUrl, setGeneratedUrl] = useState<string | null>(null)
  const [shortUrl, setShortUrl] = useState<string | null>(null)
  const [isShortening, setIsShortening] = useState(false)
  const [isOpen, setIsOpen] = useState(false)
  const dropdownRef = useRef<HTMLDivElement>(null)
  const t = translations[language]

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  const cleanUrl = (inputUrl: string) => {
    try {
      // Trouve l'index de .html
      const htmlIndex = inputUrl.indexOf('.html')
      if (htmlIndex === -1) return inputUrl

      // Garde uniquement la partie jusqu'à .html inclus
      return inputUrl.substring(0, htmlIndex + 5)
    } catch (error) {
      return inputUrl
    }
  }

  const handleUrlChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const rawUrl = e.target.value
    setUrl(cleanUrl(rawUrl))
  }

  const shortenUrl = async (longUrl: string) => {
    setIsShortening(true)
    try {
      const response = await fetch(`https://tinyurl.com/api-create.php?url=${encodeURIComponent(longUrl)}`)
      if (!response.ok) throw new Error('Failed to shorten URL')
      const shortened = await response.text()
      setShortUrl(shortened)
    } catch (error) {
      toast.error(t.errorShortening, {
        duration: 3000,
        className: 'bg-dark-light text-white border border-accent/20',
        style: {
          background: '#332D4F',
          color: '#FFFFFF',
        },
      })
    } finally {
      setIsShortening(false)
    }
  }

  const generateLink = (e: React.FormEvent) => {
    e.preventDefault()
    
    if (!url || !affId) {
      toast.error(t.fillAllFields, {
        duration: 3000,
        className: 'bg-dark-light text-white border border-accent/20',
        style: {
          background: '#332D4F',
          color: '#FFFFFF',
        },
      })
      return
    }

    try {
      const cleanedUrl = cleanUrl(url)
      const newUrl = `https://affiliate.geekbuying.com/deeplink.php?affid=${affId}&url=${encodeURIComponent(cleanedUrl)}`
      setGeneratedUrl(newUrl)
      setShortUrl(null) // Reset short URL when generating new link
      shortenUrl(newUrl) // Automatically generate short URL
    } catch (error) {
      toast.error(t.errorGenerating, {
        duration: 3000,
        className: 'bg-dark-light text-white border border-white/10'
      })
    }
  }

  return (
    <div className="min-h-screen bg-dark flex flex-col">
      <Toaster position="top-right" />
      <header className="border-b border-white/10 bg-dark-light">
        <div className="mx-auto max-w-7xl px-4 py-4 flex items-center justify-between">
          <h1 className="text-xl font-semibold text-white">{t.title}</h1>
          <div className="relative" ref={dropdownRef}>
            <button
              type="button"
              onClick={() => setIsOpen(!isOpen)}
              className="flex items-center gap-2 bg-dark text-white px-4 py-2 rounded-lg border border-white/10 focus:border-primary focus:ring-1 focus:ring-primary cursor-pointer"
            >
              <img 
                src={flagUrls[language]} 
                alt={`${language} flag`}
                className="w-5 h-5 rounded-full object-cover object-center"
              />
              {language.toUpperCase()}
            </button>

            <div 
              className={`${isOpen ? 'block' : 'hidden'} absolute right-0 mt-2 py-2 w-32 bg-dark-light rounded-lg border border-white/10 shadow-lg z-50`}
            >
              {Object.entries(flagUrls).map(([lang, flagUrl]) => (
                <button
                  key={lang}
                  onClick={() => {
                    setLanguage(lang as Language)
                    setIsOpen(false)
                  }}
                  className="w-full flex items-center gap-2 px-4 py-2 hover:bg-dark text-white text-left"
                >
                  <img 
                    src={flagUrl} 
                    alt={`${lang} flag`}
                    className="w-5 h-5 rounded-full object-cover object-center"
                  />
                  {lang.toUpperCase()}
                </button>
              ))}
            </div>
          </div>
        </div>
      </header>

      <main className="flex-1 mx-auto w-full max-w-7xl px-4 py-12">
        <div className="text-center mb-8">
          <h2 className="text-3xl font-bold text-white mb-3">{t.mainTitle}</h2>
          <p className="text-lg text-white/80 max-w-2xl mx-auto">{t.subtitle}</p>
        </div>

        <div className="rounded-2xl bg-dark-light p-8 shadow-lg">
          <article
            itemScope
            itemType="https://schema.org/SoftwareApplication"
          >
            <meta itemProp="name" content="GeekLink Generator" />
            <meta itemProp="applicationCategory" content="UtilityApplication" />
            <meta itemProp="operatingSystem" content="Web" />
            
            <form className="space-y-6" onSubmit={generateLink}>
              <div>
                <label htmlFor="affid" className="block text-sm font-medium text-white/90">
                  {t.affiliateId}
                </label>
                <input
                  type="text"
                  id="affid"
                  value={affId}
                  onChange={(e) => setAffId(e.target.value)}
                  className="mt-2 block w-full rounded-lg border border-white/10 bg-dark px-4 py-3 text-white placeholder-white/40 focus:border-primary focus:ring-1 focus:ring-primary"
                  placeholder={t.affiliateIdPlaceholder}
                />
              </div>

              <div>
                <label htmlFor="url" className="block text-sm font-medium text-white/90">
                  {t.url}
                </label>
                <input
                  type="url"
                  id="url"
                  value={url}
                  onChange={handleUrlChange}
                  className="mt-2 block w-full rounded-lg border border-white/10 bg-dark px-4 py-3 text-white placeholder-white/40 focus:border-primary focus:ring-1 focus:ring-primary"
                  placeholder={t.urlPlaceholder}
                />
              </div>

              <button
                type="submit"
                className="w-full rounded-lg bg-primary py-3 text-white font-medium hover:bg-primary-dark transition-colors focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 focus:ring-offset-dark"
              >
                {t.generate}
              </button>

              {generatedUrl && (
                <div className="mt-6 space-y-6">
                  <div className="space-y-3">
                    <label className="block text-sm font-medium text-white/90">
                      {t.generatedLink}
                    </label>
                    <div className="flex gap-2">
                      <input
                        type="text"
                        readOnly
                        value={generatedUrl}
                        className="flex-1 rounded-lg border border-white/10 bg-dark px-4 py-3 text-white"
                      />
                      <button
                        type="button"
                        onClick={() => {
                          navigator.clipboard.writeText(generatedUrl)
                          toast.success(t.copied, {
                            duration: 2000,
                            className: 'bg-dark-light text-white border border-primary/20',
                            style: {
                              background: '#332D4F',
                              color: '#FFFFFF',
                              border: '1px solid rgba(139, 92, 246, 0.2)',
                            },
                            iconTheme: {
                              primary: '#8B5CF6',
                              secondary: '#332D4F',
                            },
                          })
                        }}
                        className="px-4 rounded-lg bg-primary hover:bg-primary-dark transition-colors"
                      >
                        {t.copy}
                      </button>
                    </div>
                  </div>

                  <div className="space-y-3">
                    <label className="block text-sm font-medium text-white/90">
                      {t.shortLink}
                    </label>
                    <div className="flex gap-2">
                      <input
                        type="text"
                        readOnly
                        value={isShortening ? t.shortening : (shortUrl || '')}
                        className="flex-1 rounded-lg border border-white/10 bg-dark px-4 py-3 text-white"
                      />
                      <button
                        type="button"
                        onClick={() => {
                          if (shortUrl) {
                            navigator.clipboard.writeText(shortUrl)
                            toast.success(t.copied, {
                              duration: 2000,
                              className: 'bg-dark-light text-white border border-primary/20',
                              style: {
                                background: '#332D4F',
                                color: '#FFFFFF',
                                border: '1px solid rgba(139, 92, 246, 0.2)',
                              },
                              iconTheme: {
                                primary: '#8B5CF6',
                                secondary: '#332D4F',
                              },
                            })
                          }
                        }}
                        disabled={!shortUrl || isShortening}
                        className="px-4 rounded-lg bg-primary hover:bg-primary-dark transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {t.copy}
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </form>
          </article>
        </div>

        <AdBanner 
          slot="VOTRE_ID_EMPLACEMENT_2"
          className="mt-8"
        />
      </main>

      <footer className="mt-auto border-t border-white/10 bg-dark-light py-8">
        <div className="mx-auto max-w-7xl px-4">
          <p className="text-center text-sm text-white/60">
            © {new Date().getFullYear()} GeekLink Generator. {t.footerText}
          </p>
        </div>
      </footer>
    </div>
  )
}

export default App